import React, { useState } from 'react';
import { useMemoryBankNotes } from 'src/api/core/ai/memory-bank/queries';
import {
  useUpdateMemoryBankNote,
  useDeleteMemoryBankNote,
} from 'src/api/core/ai/memory-bank/mutations';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { Badge } from 'src/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { NoteEditForm } from 'src/components/memory-bank/NoteEditForm';
import { NoteDisplay } from 'src/components/memory-bank/NoteDisplay';
import { ImproveNoteDialog } from 'src/components/memory-bank/ImproveNoteDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import { useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { formatDistanceToNow } from 'date-fns';
import type { MemoryBankNote, MemoryBankNoteMetadata } from '@erp/rpc';
import {
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  TrashIcon,
  LightningIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import { useToast } from '@/context/ToastContext';
import { useEagerAndDebouncedState } from '@/hooks/useEagerDebouncedState';
import { useNoteActor } from '@/hooks/useNoteActor';
import { useWorkflowStepFilter } from '@/hooks/useWorkflowStepFilter';
import { useWorkflowFilter } from '@/hooks/useWorkflowFilter';
import { WorkflowFilter } from 'src/components/memory-bank/WorkflowFilter';
import { WorkflowStepFilter } from 'src/components/memory-bank/WorkflowStepFilter';
import { MemoryBankNoteMetadatum } from '@/components/memory-bank/MemoryBankNoteMetadatum';

function NoteActorDisplay({
  note,
  versionUsed,
  timestamp,
}: {
  note: MemoryBankNote;
  versionUsed: number;
  timestamp: string;
}) {
  const actor = useNoteActor(versionUsed, note);

  return (
    <div className="text-sm text-gray-500">
      <div>
        {formatDistanceToNow(new Date(timestamp), {
          addSuffix: true,
        })}
      </div>
      {actor && <div className="text-xs text-gray-400 mt-1">{actor}</div>}
    </div>
  );
}

export function MemoryBankNotesListingPage() {
  const {
    eagerValue: search,
    debouncedValue: debouncedSearch,
    setValue: setSearch,
  } = useEagerAndDebouncedState('', 300);
  const { selectedWorkflow, setSelectedWorkflow, filteredWorkflow } =
    useWorkflowFilter();
  const {
    selectedWorkflowStep,
    setSelectedWorkflowStep,
    filteredWorkflowStep,
  } = useWorkflowStepFilter(selectedWorkflow);
  const [currentPage, setCurrentPage] = useState(0);
  const [editingNote, setEditingNote] = useState<MemoryBankNote | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [improvingNote, setImprovingNote] = useState<MemoryBankNote | null>(
    null,
  );

  const pageSize = 20;
  const { showToast } = useToast();

  // Reset current page when search or filters change
  React.useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, filteredWorkflow, filteredWorkflowStep]);

  const {
    data: notesData,
    isLoading,
    error,
  } = useMemoryBankNotes({
    limit: pageSize,
    offset: currentPage * pageSize,
    search: debouncedSearch || undefined,
    workflow: filteredWorkflow,
    workflowStep: filteredWorkflowStep,
  });

  const updateNoteMutation = useUpdateMemoryBankNote();
  const deleteNoteMutation = useDeleteMemoryBankNote();

  const invalidateQueries = useInvalidateRpcQueryFn((orpc) => [
    orpc.memoryBank.list.key(),
    orpc.memoryBank.get.key(),
    orpc.memoryBank.search.key(),
    orpc.memoryBank.stats.key(),
  ]);

  const handleEdit = (note: MemoryBankNote) => {
    setEditingNote(note);
  };

  const handleSaveNote = async (
    noteContent: string,
    filteredMetadata: Partial<MemoryBankNoteMetadata>,
  ) => {
    if (!editingNote) return;

    try {
      await updateNoteMutation.mutateAsync({
        id: editingNote.id,
        data: { note: noteContent, metadata: filteredMetadata },
      });
      setEditingNote(null);
      invalidateQueries();
      showToast({
        title: 'Success',
        message: 'Note updated successfully',
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: 'Error',
        message: 'Failed to update note',
        color: 'error',
        icon: 'error',
      });
      throw error; // Re-throw so NoteEditor can handle loading states
    }
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
  };

  const handleDelete = async (noteId: string) => {
    try {
      await deleteNoteMutation.mutateAsync({ id: noteId });
      setDeletingNoteId(null);
      invalidateQueries();
      showToast({
        title: 'Success',
        message: 'Note deleted successfully',
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: 'Error',
        message: 'Failed to delete note',
        color: 'error',
        icon: 'error',
      });
    }
  };

  const handleImprove = (noteId: string) => {
    const note = notesData?.notes.find((n) => n.id === noteId);
    if (note) {
      setImprovingNote(note);
    }
  };

  const handleCloseImproveModal = () => {
    setImprovingNote(null);
  };

  const totalPages = notesData
    ? Math.ceil(notesData.pagination.total / pageSize)
    : 0;
  const hasNextPage = notesData?.pagination.hasMore ?? false;
  const hasPreviousPage = currentPage > 0;

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-red-600">Error</CardTitle>
            <CardDescription>Failed to load memory bank notes</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Memory Bank Notes</h1>
          <p className="text-gray-600 mt-1">
            Manage and search through your AI memory bank notes
          </p>
        </div>
        {notesData && (
          <Badge variant="secondary" className="text-sm">
            {notesData.pagination.total} total notes
          </Badge>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search notes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <WorkflowFilter
              selectedWorkflow={selectedWorkflow}
              onWorkflowChange={setSelectedWorkflow}
              className="sm:w-auto"
            />
            <WorkflowStepFilter
              selectedWorkflowStep={selectedWorkflowStep}
              onWorkflowStepChange={setSelectedWorkflowStep}
              className="sm:w-auto"
            />
          </div>
        </CardContent>
      </Card>

      {/* Notes Table */}
      <Card>
        <CardHeader>
          <CardTitle>Notes</CardTitle>
          <CardDescription>
            All memory bank notes with their metadata and timestamps
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : notesData?.notes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {search
                ? 'No notes found matching your search.'
                : 'No notes available.'}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40%]">Content</TableHead>
                    <TableHead>Context</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notesData?.notes.map((note) => (
                    <TableRow key={note.id}>
                      <TableCell className="max-w-md">
                        <NoteDisplay
                          content={note.note}
                          maxHeight="max-h-20"
                          showFullContent={false}
                          className="text-sm"
                        />
                      </TableCell>
                      <TableCell>
                        {note?.metadata && (
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(note.metadata).map(
                              ([key, value]) => (
                                <MemoryBankNoteMetadatum
                                  name={key}
                                  value={value}
                                  key={key}
                                />
                              ),
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <NoteActorDisplay
                          note={note}
                          versionUsed={Math.max(
                            0,
                            (note.editLog?.length || 1) - 1,
                          )}
                          timestamp={note.updatedAt.toString()}
                        />
                      </TableCell>
                      <TableCell>
                        <NoteActorDisplay
                          note={note}
                          versionUsed={0}
                          timestamp={note.createdAt.toString()}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleImprove(note.id)}
                            disabled={improvingNote?.id === note.id}
                            title="AI Improve"
                          >
                            {improvingNote?.id === note.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                            ) : (
                              <LightningIcon className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(note)}
                            title="Edit"
                          >
                            <PencilSimpleIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeletingNoteId(note.id)}
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    Page {currentPage + 1} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage(Math.max(0, currentPage - 1))
                      }
                      disabled={!hasPreviousPage}
                    >
                      <CaretLeftIcon className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!hasNextPage}
                    >
                      Next
                      <CaretRightIcon className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingNote} onOpenChange={handleCancelEdit}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
            <DialogDescription>
              Make changes to your memory bank note below using the rich text
              editor.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {editingNote && (
              <NoteEditForm
                initialValue={editingNote.note}
                metadata={editingNote.metadata}
                onSave={handleSaveNote}
                onCancel={handleCancelEdit}
                isSubmitting={updateNoteMutation.isPending}
                placeholder="Enter note content..."
                height={300}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Improve Modal */}
      <ImproveNoteDialog
        noteId={improvingNote?.id || null}
        open={!!improvingNote}
        onOpenChange={handleCloseImproveModal}
        options={{
          note: improvingNote || undefined,
          onSuccess: () => {
            invalidateQueries();
            handleCloseImproveModal();
          },
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletingNoteId}
        onOpenChange={() => setDeletingNoteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              note from the memory bank.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingNoteId && handleDelete(deletingNoteId)}
              disabled={deleteNoteMutation.isPending}
            >
              {deleteNoteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
